
import { Vue, Options } from 'vue-class-component'
import { Prop, Watch } from 'vue-property-decorator'
import { maska } from 'maska'
import cloneDeep from 'lodash/cloneDeep'
import { ICard } from 'bot-flow-maker/src/types'
import DateRangeSelectorV2 from '@/components/common/ui/DateRangeSelectorV2.vue'
import { localStore } from '@/utils/localstore'
import { constant } from '@/utils/constants'
import { IDateRangePicker } from '@/utils/types'

@Options({
  components: { DateRangeSelectorV2 },
  directives: { maska },
  emits: [
    'update:modelValue',
    'update:onReUndoCards',
    'update:openSelectNextQuestion',
    'update:openSelectNextMessage',
    'update:openSelectNextGoal',
    'update:openSelectNextForm',
    'update:handleZoomInOut',
    'update:onChangeDirection',
    'update:onOpenPreview',
    'update:onOpenHistory',
    'update:initSessionScenarios',
  ],
})
export default class ScenarioHeaderSelector extends Vue {
  @Prop()
  modelValue!: IDateRangePicker

  @Prop()
  currentScenario!: ICard[]

  @Prop()
  isReport!: boolean

  @Prop()
  titleLastHistory!: string

  sessionScenarios: ICard[][] = []
  currentIndex = 0
  isChanged = true
  currentDirection = '0'
  zoomLevel = 100
  ZOOM_SPEED = 10

  get displayZoom() {
    return Math.round(this.zoomLevel)
  }

  get value() {
    return this.modelValue
  }

  set value(value: IDateRangePicker) {
    this.$emit('update:modelValue', value)
  }

  get isUndo() {
    return this.currentIndex > 0
  }

  get isRedo() {
    return this.currentIndex > 0 && this.currentIndex < this.sessionScenarios.length - 1
  }

  get appId() {
    return this.$route?.params?.appId
  }

  get campaignId() {
    return this.$route?.params?.campaignId
  }

  get isTestMode() {
    return this.$route?.params?.isTestMode === 'true'
  }

  changeDateRange(value) {
    this.value = value
  }

  @Watch('currentScenario')
  onChangeValue() {
    if (!this.isChanged || this.currentScenario.length <= 0) {
      this.isChanged = true
      return
    }
    const key = constant.LOCAL_KEYS.SESSION_SCENARIOS + this.appId + '-' + this.campaignId + '-' + this.isTestMode
    const response = localStore.getWithExpiry(key)
    if (response) {
      if (response.isTestMode !== this.isTestMode) {
        return
      }
      this.sessionScenarios = response.sessionScenarios
      this.currentIndex = response.currentIndex
      this.isChanged = response.isChanged
      const sessionScenario = this.sessionScenarios[this.sessionScenarios.length - 1]
      if (
        JSON.stringify(this.currentScenario) === JSON.stringify(sessionScenario) ||
        JSON.stringify(this.currentScenario) === JSON.stringify(this.sessionScenarios[0])
      ) {
        return
      }
    } else {
      this.sessionScenarios = []
      this.currentIndex = 0
      this.isChanged = true
    }

    this.sessionScenarios.push(cloneDeep(this.currentScenario))
    // console.log(this.currentScenario, 'currentScenario onChangeValue')
    // console.log(this.sessionScenarios, 'sessionScenarios onChangeValue')
    this.currentIndex = this.sessionScenarios.length - 1
    this.onUpdateLocalScenarios()
  }

  @Watch('currentDirection')
  onChangeDirection() {
    this.$emit('update:onChangeDirection', this.currentDirection)
  }

  onUpdateLocalScenarios() {
    const key = constant.LOCAL_KEYS.SESSION_SCENARIOS + this.appId + '-' + this.campaignId + '-' + this.isTestMode
    const resp = {
      sessionScenarios: this.sessionScenarios,
      currentIndex: this.currentIndex,
      isChanged: this.isChanged,
      isTestMode: this.isTestMode,
    }
    localStore.setWithExpiry(key, resp, constant.LOCAL_EXPIRED)
  }

  onUndo() {
    this.currentIndex = this.currentIndex - 1
    this.isChanged = false
    this.$emit('update:onReUndoCards', this.sessionScenarios[this.currentIndex])
  }

  onRedo() {
    this.currentIndex = this.currentIndex + 1
    this.isChanged = false
    this.$emit('update:onReUndoCards', this.sessionScenarios[this.currentIndex])
  }

  onZoomIn() {
    if (this.zoomLevel > 200) {
      this.zoomLevel = 200
    }
    this.zoomLevel = this.zoomLevel + this.ZOOM_SPEED
    this.$emit('update:handleZoomInOut', this.zoomLevel, 'in')
  }

  onZoomOut() {
    this.zoomLevel = this.zoomLevel - this.ZOOM_SPEED
    if (this.zoomLevel < 0) {
      this.zoomLevel = 0
    }
    this.$emit('update:handleZoomInOut', this.zoomLevel, 'out')
  }

  onExpand(side) {
    this.currentScenario.forEach((element) => {
      const card = document.getElementById(element.uniqueId) as HTMLElement
      const style = window.getComputedStyle(card)
      // eslint-disable-next-line no-undef
      const matrix = new WebKitCSSMatrix(style.transform)
      let transformX = matrix.m41
      let transformY = matrix.m42
      if (side === 'top') {
        transformY += 30
        element.top += 30
      } else {
        transformX += 30
        element.left += 30
      }
      card.style.transform = 'translate(' + transformX + 'px, ' + transformY + 'px)'
    })
    this.updateCardLine(side)
    this.onChangeValue()
  }

  updateCardLine(side) {
    const lines = document.getElementsByClassName('leader-line')
    for (let i = 0; i < lines.length; i++) {
      const line = document.getElementById(lines[i].id) as HTMLElement
      let top = parseFloat(line.style.top.replace('px', ''))
      let left = parseFloat(line.style.left.replace('px', ''))
      if (side === 'top') {
        top += 30
        line.style.top = top + 'px'
      } else {
        left += 30
        line.style.left = left + 'px'
      }
    }
  }

  openSelectNextQuestion() {
    this.$emit('update:openSelectNextQuestion', {})
  }

  openSelectNextMessage() {
    this.$emit('update:openSelectNextMessage', {})
  }

  openSelectNextGoal() {
    this.$emit('update:openSelectNextGoal', {})
  }

  openSelectNextForm() {
    this.$emit('update:openSelectNextForm', {})
  }

  onOpenHistory() {
    this.$emit('update:onOpenHistory')
  }

  onOpenPreview() {
    this.$emit('update:onOpenPreview')
  }

  setDirectionType(value) {
    this.currentDirection = value
  }

  setTileHistory(value) {
    // this.directionType = value
    this.$refs.scenarioHeader.setDirectionType(value)
  }

  resetData() {
    this.sessionScenarios = []
    this.currentIndex = 0
    this.isChanged = true
    this.currentDirection = '0'
    this.zoomLevel = 100
  }

  handleUndo() {
    if (!this.isReport && this.isUndo) {
      this.onUndo()
    }
  }

  mounted() {
    const key = constant.LOCAL_KEYS.SESSION_SCENARIOS + this.appId + '-' + this.campaignId + '-' + this.isTestMode
    const response = localStore.getWithExpiry(key)
    if (response) {
      this.sessionScenarios = response.sessionScenarios
      this.currentIndex = response.currentIndex
      this.isChanged = response.isChanged
      this.$emit('update:initSessionScenarios', this.sessionScenarios[this.currentIndex])
    }
  }
}
